import { useCallback, useRef } from 'react';
import { API_CONFIG } from '../config/constants';
import { getAuthToken } from '../../../core/auth/supabase-auth';

interface UseViolinPlotsParams {
    onComplete: (violinPlots: unknown, dataCount: number) => void;
    onError: (message: string) => void;
    resultsLoaded?: boolean;
}

interface UseViolinPlotsReturn {
    loadViolinPlots: (sessionId: string) => Promise<void>;
    loadViolinPlotsFromResults: (sessionId: string) => Promise<void>;
}

export const useViolinPlots = ({
    onComplete,
    onError,
    resultsLoaded = false
}: UseViolinPlotsParams): UseViolinPlotsReturn => {
    const mountedRef = useRef(true);

    const loadViolinPlots = useCallback(async (sessionId: string) => {
        if (resultsLoaded) {
            return;
        }
        try {
            const token = await getAuthToken();
            if (!token) {
                console.error('[loadViolinPlots] No auth token available');
                return;
            }

            // Use /visualizations endpoint for violin plots (lightweight, no pickle download)
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VISUALIZATIONS}/${sessionId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok && mountedRef.current) {
                const data = await response.json();
                // /visualizations returns plots in data.plots
                if (data.plots && Object.keys(data.plots).length > 0) {
                    onComplete(data.plots, data.n_dat || 0);
                }
            }
        } catch {
            // Error handled silently
        }
    }, [onComplete, resultsLoaded]);

    const loadViolinPlotsFromResults = useCallback(async (sessionId: string) => {
        if (resultsLoaded) {
            return;
        }
        try {
            const token = await getAuthToken();
            if (!token) {
                console.error('[loadViolinPlotsFromResults] No auth token available');
                return;
            }

            // Use /visualizations endpoint for violin plots (lightweight, no pickle download)
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VISUALIZATIONS}/${sessionId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!mountedRef.current) return;

            if (response.ok) {
                const data = await response.json();

                if (data.status === 'data_validation_error') {
                    onError('Data validation error during visualization');
                    return;
                }

                // /visualizations returns plots in data.plots
                if (data.plots && Object.keys(data.plots).length > 0) {
                    onComplete(data.plots, data.n_dat || 0);
                }
            } else {
                const errorData = await response.json();
                if (errorData.status === 'data_validation_error') {
                    onError('Data validation error during visualization');
                }
            }
        } catch {
            // Error handled silently
        }
    }, [onComplete, onError, resultsLoaded]);

    return {
        loadViolinPlots,
        loadViolinPlotsFromResults
    };
};
